import type {RefObject} from 'react';
import {useCallback, useRef, useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import {usePrevious} from '@pexip/hooks';

import {sidePanelPlusDesktopToolbarWidth} from '../constants';

export const isOverlappingSidePanel = (
    elementRect: DOMRect,
    dir: 'ltr' | 'rtl',
) => {
    if (dir === 'ltr') {
        return Math.floor(elementRect.left) <= sidePanelPlusDesktopToolbarWidth;
    }
    return (
        Math.ceil(elementRect.right) >=
        window.innerWidth - sidePanelPlusDesktopToolbarWidth
    );
};

export const useSidePanelTransform = (
    isSidePanelVisible: boolean,
    elementRef: RefObject<HTMLDivElement>,
    enabled = true,
) => {
    const prevIsSidePanelVisible = usePrevious(isSidePanelVisible);
    const lastLeft = useRef<number | undefined>();

    const setNoSidePanelLeft = useCallback((left?: number) => {
        lastLeft.current = left;
    }, []);

    const {i18n} = useTranslation();
    const dir = i18n.dir(i18n.language);

    useEffect(() => {
        if (isSidePanelVisible === prevIsSidePanelVisible) {
            return;
        }
        if (enabled && elementRef.current) {
            const element = elementRef.current;
            if (isSidePanelVisible) {
                const elementRect = element.getBoundingClientRect();
                if (isOverlappingSidePanel(elementRect, dir)) {
                    lastLeft.current = elementRect.left;
                    if (dir === 'ltr') {
                        element.style.transform = `translate(${
                            sidePanelPlusDesktopToolbarWidth -
                            element.offsetLeft
                        }px)`;
                    } else {
                        element.style.transform = `translate(${
                            window.innerWidth -
                            sidePanelPlusDesktopToolbarWidth -
                            elementRect.right
                        }px)`;
                    }
                }
            } else {
                if (lastLeft.current) {
                    element.style.transform = `translate(${
                        lastLeft.current - element.offsetLeft
                    }px)`;
                    lastLeft.current = undefined;
                }
            }
        }
    }, [enabled, elementRef, isSidePanelVisible, prevIsSidePanelVisible, dir]);

    return setNoSidePanelLeft;
};
